import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Phone, PhoneCall, UserCheck, Calendar, X, ExternalLink, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { useTelefono, BotonesLlamar } from './Softphone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Llamadas: la bandeja de "a quién hay que llamar hoy".
//
// Aquí caen los particulares que han AUTORIZADO que les llamemos. La llamada la
// hace una persona con el softphone; esta pantalla sirve para repartirlas y para
// volcar lo que pasó. Al volcarla, la IA lee la conversación y decide si esa
// persona entra en la cartera de vendedores y con qué estado — así nadie tiene
// que rellenar una ficha a mano después de colgar.

const EUR = (n) => (n == null ? '—' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n));
const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const COLOR = {
  pendiente: '#c9a84c', asignada: '#06b6d4', efectuada: '#10b981',
  no_contesta: '#f59e0b', descartada: '#64748b',
  alto: '#10b981', medio: '#c9a84c', bajo: '#f59e0b', ninguno: '#ef4444',
};

// Marcar un numero a mano. La bandeja de abajo sirve para las llamadas que ya
// estan pendientes; esto es para cuando hay que llamar a alguien que no esta en
// ninguna lista todavia.
function Marcador() {
  const tel = useTelefono();
  const [num, setNum] = React.useState('');
  const [quien, setQuien] = React.useState('Marcos');

  if (!tel?.disponible) {
    return (
      <div className="rounded-xl border border-white/8 bg-white/4 p-4">
        <p className="text-white/40 text-[11px] uppercase tracking-wide">Marcador</p>
        <p className="text-white/45 text-sm mt-1">
          El teléfono no está disponible en esta sesión.
        </p>
      </div>
    );
  }

  const llamar = () => {
    const limpio = num.replace(/[^\d+]/g, '');
    if (limpio.replace(/\D/g, '').length < 9) return;
    tel.llamar(limpio, { comoQuien: quien });
  };

  return (
    <div className="rounded-xl border border-white/8 bg-white/4 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0">
          <p className="text-white/40 text-[11px] uppercase tracking-wide">Marcador</p>
          <p className="text-white/45 text-xs mt-0.5">
            {tel.registrado
              ? 'Escribe un número y llama con el teléfono del CRM.'
              : 'Conectando el teléfono…'}
          </p>
        </div>
        <input
          value={num}
          onChange={(e) => setNum(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') llamar(); }}
          placeholder="600 000 000"
          inputMode="tel"
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/50 w-44"
        />
        <select value={quien} onChange={(e) => setQuien(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 focus:outline-none">
          <option value="Marcos" className="bg-[#0f172a]">como Marcos</option>
          <option value="Sergio" className="bg-[#0f172a]">como Sergio</option>
        </select>
        <button onClick={llamar} disabled={!tel.registrado || !!tel.llamada}
          className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30 flex items-center gap-1.5">
          <PhoneCall size={13} /> Llamar
        </button>
      </div>
    </div>
  );
}

export function SeccionLlamadas({ orgId }) {
  // Quien puede llamar sale de la propia organizacion: pedirlo aqui evita
  // depender de que la pagina padre lo tenga cargado.
  const [miembros, setMiembros] = useState([]);
  const [datos, setDatos] = useState({ llamadas: [], conteo: {} });
  const [filtro, setFiltro] = useState('');
  const [cargando, setCargando] = useState(true);
  const [volcando, setVolcando] = useState(null);   // la llamada abierta en el modal
  const [nueva, setNueva] = useState(null);

  const cargar = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await axios.get(`${API}/crm/llamadas?org_id=${orgId}${filtro ? `&estado=${filtro}` : ''}`);
      setDatos(r.data || { llamadas: [], conteo: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar las llamadas');
    }
    setCargando(false);
  }, [orgId, filtro]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/organizations/${orgId}/members`)
      .then((r) => setMiembros((r.data || []).map((m) => ({
        user_id: m.user_id || m.id, name: m.name || m.email || 'Sin nombre',
      }))))
      .catch(() => setMiembros([]));   // sin lista, se puede llamar igual: solo se pierde el desplegable
  }, [orgId]);
  useEffect(() => { const t = setInterval(cargar, 60000); return () => clearInterval(t); }, [cargar]);

  const asignar = async (l, quien) => {
    try {
      await axios.put(`${API}/crm/llamadas/${l.llamada_id}`, {
        estado: 'asignada', asignado_a: quien.user_id, asignado_nombre: quien.name,
      });
      toast.success(`Asignada a ${quien.name}`);
      cargar();
    } catch (err) { toast.error('No se pudo asignar'); }
  };

  const marcar = async (l, estado) => {
    try {
      await axios.put(`${API}/crm/llamadas/${l.llamada_id}`, { estado });
      cargar();
    } catch (err) { toast.error('No se pudo cambiar el estado'); }
  };


  // Borrar una llamada. Se avisa de lo que se lleva por delante: la
  // transcripcion y el resumen son el trabajo de Claude sobre esa conversacion
  // y no se pueden recuperar.
  const borrarLlamada = async (l) => {
    const quien = l.nombre || l.telefono || 'esta llamada';
    const extra = l.resumen ? '\n\nSe borrara tambien su transcripcion y su resumen.' : '';
    if (!window.confirm(`Eliminar la llamada de ${quien}?${extra}`)) return;
    try {
      await axios.delete(`${API}/crm/llamadas/${l.llamada_id}`);
      toast.success('Llamada eliminada');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  if (cargando) return <div className="p-8 text-white/40 text-sm">Cargando llamadas…</div>;

  const c = datos.conteo || {};

  return (
    <div className="space-y-5">
      <Marcador />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Por llamar', c.pendientes, '#c9a84c', <Phone size={14} key="1" />],
          ['Asignadas', c.asignadas, '#06b6d4', <UserCheck size={14} key="2" />],
          ['Hechas', c.efectuadas, '#10b981', <PhoneCall size={14} key="3" />],
          ['Visitas cerradas', c.visitas, '#ec4899', <Calendar size={14} key="4" />],
        ].map(([lbl, val, col, ico]) => (
          <div key={lbl} className="rounded-xl border border-white/8 bg-white/4 p-4">
            <p className="text-white/40 text-[11px] uppercase tracking-wide flex items-center gap-1.5" style={{ color: `${col}99` }}>{ico}{lbl}</p>
            <p className="text-2xl font-black mt-1" style={{ color: col }}>{val ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[['', 'Todas'], ['pendiente', 'Por llamar'], ['asignada', 'Asignadas'], ['efectuada', 'Hechas']].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filtro === k ? 'bg-[#c9a84c] text-black' : 'text-white/50 hover:text-white bg-white/4'}`}>
            {l}
          </button>
        ))}
        <button onClick={() => setNueva({ nombre: '', telefono: '', motivo: '' })}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/6 text-white/70 hover:text-white flex items-center gap-1.5">
          <Plus size={12} /> A mano
        </button>
        <button onClick={cargar} className="ml-auto p-2 rounded-lg text-white/40 hover:text-white" title="Actualizar"><RefreshCw size={14} /></button>
      </div>

      <div className="space-y-2">
        {datos.llamadas.length === 0 && (
          <div className="rounded-xl border border-white/8 p-10 text-center">
            <Phone size={22} className="mx-auto text-white/15 mb-2" />
            <p className="text-white/30 text-sm">No hay llamadas pendientes.</p>
            <p className="text-white/20 text-xs mt-1">Aparecen solas cuando un particular autoriza que le llamemos.</p>
          </div>
        )}

        {datos.llamadas.map((l) => (
          <div key={l.llamada_id} className="rounded-xl border border-white/8 bg-white/3 p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold">{l.nombre || 'Sin nombre'}</span>
                  {l.telefono
                    ? <a href={`tel:${l.telefono}`} className="text-[#c9a84c] text-sm font-mono hover:underline">{l.telefono}</a>
                    : <span className="text-amber-300/70 text-xs">sin teléfono — está en el chat</span>}
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ background: `${COLOR[l.estado]}22`, color: COLOR[l.estado] }}>{l.estado}</span>
                  {l.prioridad >= 10 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-300">prioritaria</span>}
                  {l.interes && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                                      style={{ background: `${COLOR[l.interes]}22`, color: COLOR[l.interes] }}>interés {l.interes}</span>}
                  <button onClick={() => borrarLlamada(l)} title="Eliminar esta llamada"
                    className="ml-auto text-white/25 hover:text-red-400 transition-colors p-1 -m-1">
                    <Trash2 size={13} />
                  </button>
                </div>

                {l.motivo && <p className="text-white/55 text-xs mt-1.5">{l.motivo}</p>}
                {l.notas && <p className="text-white/35 text-[11px] mt-1">{l.notas}</p>}

                {(l.anuncio_titulo || l.precio) && (
                  <p className="text-white/35 text-[11px] mt-1.5">
                    {l.anuncio_titulo} {l.precio ? `· ${EUR(l.precio)}` : ''} {l.zona ? `· ${l.zona}` : ''}
                    {l.anuncio_url && <a href={l.anuncio_url} target="_blank" rel="noreferrer" className="ml-1.5 text-white/30 hover:text-[#c9a84c] inline-flex"><ExternalLink size={10} /></a>}
                  </p>
                )}

                {l.estado === 'efectuada' && (
                  <div className="mt-2.5 rounded-lg bg-white/4 border border-white/8 p-2.5">
                    <p className="text-white/70 text-xs">{l.resumen}</p>
                    <p className="text-white/35 text-[11px] mt-1">
                      {fecha(l.llamada_el)}
                      {l.duracion_seconds ? ` · ${Math.round(l.duracion_seconds / 60)} min` : ''}
                      {l.resultado ? ` · ${l.resultado.replace('_', ' ')}` : ''}
                      {l.visita_el ? ` · visita ${fecha(l.visita_el)}` : ''}
                    </p>
                  </div>
                )}

                {l.asignado_nombre && <p className="text-white/30 text-[11px] mt-1.5">Lleva: {l.asignado_nombre}</p>}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {l.estado !== 'efectuada' && miembros.length > 0 && (
                  <select onChange={(e) => { const m = miembros.find((x) => x.user_id === e.target.value); if (m) asignar(l, m); }}
                    value={l.asignado_a || ''} className="bg-white/6 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/70">
                    <option value="">Asignar a…</option>
                    {miembros.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                  </select>
                )}
                {l.estado !== 'efectuada' && (
                  <>
                    <motion.button whileTap={{ scale: 0.95 }} onClick={() => setVolcando(l)}
                      className="px-3 py-1.5 rounded-lg bg-[#c9a84c] text-black text-xs font-bold">
                      Ya he llamado
                    </motion.button>
                    <button onClick={() => marcar(l, 'no_contesta')}
                      className="px-3 py-1.5 rounded-lg bg-white/6 text-white/60 hover:text-white text-xs font-semibold">No contesta</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {volcando && <ModalResultado llamada={volcando} onCerrar={() => setVolcando(null)} onHecho={() => { setVolcando(null); cargar(); }} />}
      {nueva && <ModalNueva orgId={orgId} onCerrar={() => setNueva(null)} onHecho={() => { setNueva(null); cargar(); }} />}
    </div>
  );
}

// Volcar la llamada. Lo que se escriba aquí lo lee la IA y de ahí sale la ficha
// del vendedor: por eso el campo pide la conversación, no un "ok/ko".
function ModalResultado({ llamada, onCerrar, onHecho }) {
  const [texto, setTexto] = useState('');
  const [minutos, setMinutos] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [lectura, setLectura] = useState(null);

  const guardar = async () => {
    if (!texto.trim()) { toast.error('Cuenta qué pasó en la llamada'); return; }
    setGuardando(true);
    try {
      const r = await axios.post(`${API}/crm/llamadas/${llamada.llamada_id}/resultado`, {
        notas: texto.trim(),
        duracion_seconds: minutos ? Math.round(Number(minutos) * 60) : null,
      });
      setLectura(r.data);
      if (r.data.seller_id) toast.success('Ficha de vendedor creada');
      else toast.success('Llamada registrada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onCerrar}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12100c] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-black">Llamada a {llamada.nombre}</h3>
            <p className="text-white/40 text-xs">{llamada.telefono || 'sin teléfono'}</p>
          </div>
          <button onClick={onCerrar} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        {!lectura ? (
          <>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={7}
              placeholder="Cuenta la llamada como si se la contaras a un compañero: qué te ha dicho, por qué vende, si habéis quedado y cuándo. Cuanto más natural, mejor lo entiende."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 placeholder-white/25 resize-none focus:outline-none focus:border-[#c9a84c]/50" />
            <div className="flex items-center gap-3 mt-3">
              <input type="number" value={minutos} onChange={(e) => setMinutos(e.target.value)} placeholder="minutos"
                className="w-28 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 placeholder-white/25" />
              <motion.button whileTap={{ scale: 0.97 }} onClick={guardar} disabled={guardando}
                className="ml-auto px-5 py-2 rounded-lg bg-[#c9a84c] text-black font-bold text-sm disabled:opacity-40">
                {guardando ? 'Leyendo…' : 'Guardar'}
              </motion.button>
            </div>
            <p className="text-white/25 text-[11px] mt-3">
              Con esto la IA decide el interés y, si lo hay, crea la ficha del vendedor con su estado. Si no lo hay, se cierra sin ensuciar la cartera.
            </p>
          </>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg bg-white/4 border border-white/8 p-3">
              <p className="text-white/80 text-sm">{lectura.lectura?.resumen}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ background: `${COLOR[lectura.lectura?.interes]}22`, color: COLOR[lectura.lectura?.interes] }}>
                  interés {lectura.lectura?.interes}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-white/60">
                  {(lectura.lectura?.resultado || '').replace('_', ' ')}
                </span>
                {lectura.llamada?.visita_el && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-pink-500/15 text-pink-300">
                    visita {fecha(lectura.llamada.visita_el)}
                  </span>
                )}
              </div>
            </div>
            {lectura.seller_id
              ? <p className="text-emerald-300 text-sm">Ya está en la cartera de vendedores.</p>
              : <p className="text-white/40 text-sm">No se ha creado ficha: no había interés suficiente.</p>}
            <button onClick={onHecho} className="w-full py-2.5 rounded-lg bg-[#c9a84c] text-black font-bold text-sm">Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalNueva({ orgId, onCerrar, onHecho }) {
  const [f, setF] = useState({ nombre: '', telefono: '', motivo: '' });
  const [g, setG] = useState(false);
  const crear = async () => {
    if (!f.nombre.trim() && !f.telefono.trim()) { toast.error('Pon al menos el nombre o el teléfono'); return; }
    setG(true);
    try {
      await axios.post(`${API}/crm/llamadas`, { ...f, organization_id: orgId, origen: 'manual' });
      toast.success('Añadida a la bandeja');
      onHecho();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo crear'); }
    setG(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onCerrar}>
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#12100c] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-black">Añadir una llamada</h3>
          <button onClick={onCerrar} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {[['nombre', 'Nombre'], ['telefono', 'Teléfono'], ['motivo', 'Por qué hay que llamar']].map(([k, ph]) => (
          <input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph}
            className="w-full mb-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/50" />
        ))}
        <button onClick={crear} disabled={g} className="w-full mt-2 py-2.5 rounded-lg bg-[#c9a84c] text-black font-bold text-sm disabled:opacity-40">
          {g ? 'Guardando…' : 'Añadir'}
        </button>
      </div>
    </div>
  );
}

export default SeccionLlamadas;
